import React, {useMemo, useRef, useState, useEffect} from "react";
import {useFrame} from "@react-three/fiber";
import * as THREE from "three";

export function Fireflies({ time }) {
    const fireflyCount = 30;
    const pointsRef = useRef();

    // Boolean check for night time
    const isNight = time < 0.25 || time > 0.75;

    // Instead of opacity as an attribute, use color with alpha
    const fireflyData = useMemo(() => {
        const positions = new Float32Array(fireflyCount * 3);
        const scales = new Float32Array(fireflyCount);
        const colors = new Float32Array(fireflyCount * 4); // RGBA colors
        const phases = new Float32Array(fireflyCount);
        const speeds = new Float32Array(fireflyCount);
        const basePositions = new Float32Array(fireflyCount * 3);

        // Firefly base color (yellow-orange glow)
        const baseColor = new THREE.Color("#ffcc66");

        for (let i = 0; i < fireflyCount; i++) {
            // Random position within garden bounds
            const x = (Math.random() - 0.5) * 30;
            const y = 0.5 + Math.random() * 2; // Height above ground
            const z = (Math.random() - 0.5) * 30;

            const i3 = i * 3;
            const i4 = i * 4;

            basePositions[i3] = x;
            basePositions[i3 + 1] = y;
            basePositions[i3 + 2] = z;

            // Initial positions
            positions[i3] = x;
            positions[i3 + 1] = y;
            positions[i3 + 2] = z;

            // Initial scale
            scales[i] = 0.5 + Math.random() * 0.5;

            // Initial color with zero alpha (invisible)
            colors[i4] = baseColor.r;
            colors[i4 + 1] = baseColor.g;
            colors[i4 + 2] = baseColor.b;
            colors[i4 + 3] = 0; // Alpha channel (0 = transparent)

            // Random phase for throbbing
            phases[i] = Math.random() * Math.PI * 2;

            // Random speed for movement
            speeds[i] = 0.2 + Math.random() * 0.3;
        }

        return { positions, scales, colors, phases, speeds, basePositions };
    }, []);

    // Firefly lifecycle state
    const lifecycleRef = useRef(Array(fireflyCount).fill().map(() => ({
        active: false,
        lifespan: 5 + Math.random() * 10, // 5-15 seconds lifespan
        age: 0,
        activationDelay: Math.random() * 5,
        waitingToActivate: false
    })));

    // Calculate visible count same as before...
    const visibleCount = useMemo(() => {
        if (!isNight) return 0;

        let nightDepth;
        if (time < 0.25) {
            nightDepth = 1 - (time / 0.25);
        } else {
            nightDepth = (1 - time) / 0.25;
        }

        nightDepth = Math.pow(nightDepth, 2);
        return Math.floor(fireflyCount * nightDepth);
    }, [time, isNight]);

    const [activeFireflies, setActiveFireflies] = useState([]);
    const maxLights = 8;
    const prevNightRef = useRef(isNight);

    // Animation loop
    useFrame((state, delta) => {
        if (!pointsRef.current) return;

        // Handle day/night transitions
        if (isNight !== prevNightRef.current) {
            if (isNight) {
                // Reset fireflies at sunset
                for (let i = 0; i < fireflyCount; i++) {
                    const lifecycle = lifecycleRef.current[i];
                    lifecycle.active = false;
                    lifecycle.age = 0;
                    lifecycle.waitingToActivate = i < visibleCount;
                    lifecycle.activationDelay = Math.random() * 5;
                    lifecycle.lifespan = 5 + Math.random() * 10;
                }
            } else {
                // Deactivate all at dawn
                for (let i = 0; i < fireflyCount; i++) {
                    lifecycleRef.current[i].active = false;
                    lifecycleRef.current[i].waitingToActivate = false;
                }

                // Clear all alphas in color array
                if (pointsRef.current) {
                    const colors = pointsRef.current.geometry.attributes.color.array;
                    for (let i = 0; i < fireflyCount; i++) {
                        colors[i * 4 + 3] = 0; // Set alpha to 0
                    }
                    pointsRef.current.geometry.attributes.color.needsUpdate = true;
                }

                setActiveFireflies([]);
            }

            prevNightRef.current = isNight;
        }

        if (!isNight) return;

        const time = state.clock.elapsedTime;
        const positions = pointsRef.current.geometry.attributes.position.array;
        const scales = pointsRef.current.geometry.attributes.scale.array;
        const colors = pointsRef.current.geometry.attributes.color.array;

        let needsUpdate = false;
        const newActiveFireflies = [];

        // Update fireflies
        for (let i = 0; i < fireflyCount; i++) {
            const i3 = i * 3;
            const i4 = i * 4;
            const phase = fireflyData.phases[i];
            const speed = fireflyData.speeds[i];
            const lifecycle = lifecycleRef.current[i];

            // Lifecycle management
            if (i < visibleCount) {
                if (lifecycle.waitingToActivate) {
                    lifecycle.activationDelay -= delta;
                    if (lifecycle.activationDelay <= 0) {
                        lifecycle.active = true;
                        lifecycle.age = 0;
                        lifecycle.waitingToActivate = false;
                        needsUpdate = true;
                    }
                }
                else if (!lifecycle.active && !lifecycle.waitingToActivate) {
                    lifecycle.waitingToActivate = true;
                    lifecycle.activationDelay = Math.random() * 5;
                }
            } else if (lifecycle.waitingToActivate) {
                lifecycle.waitingToActivate = false;
            }

            // Process active fireflies
            if (lifecycle.active) {
                lifecycle.age += delta;

                if (lifecycle.age >= lifecycle.lifespan) {
                    lifecycle.active = false;
                    colors[i4 + 3] = 0; // Set alpha to 0
                    needsUpdate = true;

                    if (i < visibleCount) {
                        lifecycle.waitingToActivate = true;
                        lifecycle.activationDelay = Math.random() * 5;
                        lifecycle.lifespan = 5 + Math.random() * 10;
                    }

                    continue;
                }

                // Calculate fade factor
                const fadeInTime = lifecycle.lifespan * 0.1;
                const fadeOutTime = lifecycle.lifespan * 0.1;
                let fadeFactor = 1.0;

                if (lifecycle.age < fadeInTime) {
                    fadeFactor = lifecycle.age / fadeInTime;
                } else if (lifecycle.age > lifecycle.lifespan - fadeOutTime) {
                    fadeFactor = (lifecycle.lifespan - lifecycle.age) / fadeOutTime;
                }

                fadeFactor = Math.max(0, Math.min(1, fadeFactor));

                // Animation updates
                const pulse = Math.sin(time * 2 + phase) * 0.2 + 0.8;
                scales[i] = (0.5 + Math.random() * 0.5) * pulse;

                // Set alpha in color array
                colors[i4 + 3] = 0.8 * fadeFactor;

                // Position updates
                const baseX = fireflyData.basePositions[i3];
                const baseY = fireflyData.basePositions[i3 + 1];
                const baseZ = fireflyData.basePositions[i3 + 2];

                positions[i3] = baseX + Math.sin(time * speed + phase) * 1.5;
                positions[i3 + 1] = baseY + Math.cos(time * speed * 0.7 + phase) * 0.5;
                positions[i3 + 2] = baseZ + Math.sin(time * speed * 0.5 + phase + 1) * 1.5;

                if (fadeFactor > 0.3 && newActiveFireflies.length < maxLights) {
                    newActiveFireflies.push({
                        position: [positions[i3], positions[i3 + 1], positions[i3 + 2]],
                        intensity: 0.2 * fadeFactor,
                        phase: phase
                    });
                }

                needsUpdate = true;
            } else if (colors[i4 + 3] > 0) {
                // Ensure inactive fireflies are fully transparent
                colors[i4 + 3] = 0;
                needsUpdate = true;
            }
        }

        if (needsUpdate) {
            pointsRef.current.geometry.attributes.position.needsUpdate = true;
            pointsRef.current.geometry.attributes.scale.needsUpdate = true;
            pointsRef.current.geometry.attributes.color.needsUpdate = true;
            setActiveFireflies(newActiveFireflies);
        }
    });

    if (!isNight) {
        return null;
    }

    return (
        <>
            <points ref={pointsRef}>
                <bufferGeometry>
                    <bufferAttribute
                        attach="attributes-position"
                        count={fireflyCount}
                        array={fireflyData.positions}
                        itemSize={3}
                    />
                    <bufferAttribute
                        attach="attributes-scale"
                        count={fireflyCount}
                        array={fireflyData.scales}
                        itemSize={1}
                    />
                    <bufferAttribute
                        attach="attributes-color"
                        count={fireflyCount}
                        array={fireflyData.colors}
                        itemSize={4}
                    />
                </bufferGeometry>
                <pointsMaterial
                    size={0.15}
                    vertexColors={true}
                    transparent={true}
                    blending={THREE.AdditiveBlending}
                    sizeAttenuation={true}
                    depthWrite={false}
                />
            </points>

            {activeFireflies.map((firefly, i) => (
                <pointLight
                    key={i}
                    position={firefly.position}
                    distance={4}
                    intensity={firefly.intensity}
                    color="#ffcc66"
                    castShadow={false}
                />
            ))}
        </>
    );
}
